import { Text, makeStyles, tokens } from '@fluentui/react-components';
import { History24Regular } from '@fluentui/react-icons';
import {
  formatStatusHistoryDate,
  toStatusHistoryNodes,
  type IWorkStatusHistoryItem,
} from '@moko/utils';
import { useTranslation } from 'react-i18next';
import Empty from '../Empty/Empty';
import useFetch from '../hooks/useFetch';
import { statusColors, type IStatusOption } from '../StatusBadge';
import type { IWorkStatusHistoryServices, WorkComponentProps } from './types';
import ContentSkeleton from '../ContentSkeleton/ContentSkeleton';

export interface WorkStatusHistoryProps extends WorkComponentProps {
  workId: number;
  services: IWorkStatusHistoryServices;
  statuses?: IStatusOption[];
}

const useStyles = makeStyles({
  root: {
    display: 'flex',
    flexDirection: 'column',
    paddingTop: tokens.spacingVerticalL,
    paddingBottom: tokens.spacingVerticalL,
    maxWidth: '480px',
  },
  item: {
    display: 'flex',
    alignItems: 'stretch',
    columnGap: tokens.spacingHorizontalM,
  },
  rail: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    width: '16px',
    flexShrink: 0,
  },
  dot: {
    width: '10px',
    height: '10px',
    borderRadius: '50%',
    boxSizing: 'border-box',
    border: `2px solid ${tokens.colorBrandForeground1}`,
    backgroundColor: tokens.colorBrandForeground1,
    marginTop: '6px',
    flexShrink: 0,
  },
  line: {
    width: '2px',
    flexGrow: 1,
    minHeight: '16px',
    backgroundColor: tokens.colorNeutralStroke2,
    marginTop: '4px',
  },
  body: {
    minWidth: 0,
    paddingBottom: tokens.spacingVerticalL,
    display: 'flex',
    flexDirection: 'column',
    rowGap: tokens.spacingVerticalXXS,
  },
  meta: {
    color: tokens.colorNeutralForeground2,
  },
});

const WorkStatusHistory = ({
  workId,
  services,
  statuses = [],
  setIsInProgress,
  setErrorMessage,
}: WorkStatusHistoryProps) => {
  const { t } = useTranslation();
  const styles = useStyles();
  const historyFetch = useFetch<IWorkStatusHistoryItem[]>({
    queryKey: [workId, 'statusHistory'],
    queryFn: (abortController) =>
      services.getWorkStatusHistories(abortController, workId),
    onLoading: () => setIsInProgress(true),
    onLoaded: () => setIsInProgress(false),
    onError: (error) => {
      setIsInProgress(false);
      setErrorMessage(
        error.message ||
          t('failedToLoadStatusHistory', 'Failed to load status history.')
      );
    },
  });

  if (historyFetch.loading && !historyFetch.data) {
    return <ContentSkeleton variant="list" />;
  }

  const nodes = toStatusHistoryNodes(historyFetch.data ?? []);
  if (nodes.length === 0) {
    return (
      <Empty
        icon={<History24Regular />}
        title={t('noStatusHistory', 'No status history yet')}
        description={t(
          'noStatusHistoryDescription',
          'Status changes for this work will appear here.'
        )}
      />
    );
  }

  return (
    <div className={styles.root}>
      {nodes.map((node, index) => {
        const status = statuses.find((item) => item.id === node.statusId);
        const { background } = statusColors(status?.color);
        const isLast = index === nodes.length - 1;

        return (
          <div key={node.key} className={styles.item}>
            <div className={styles.rail}>
              <span
                className={styles.dot}
                style={{
                  backgroundColor: background,
                  borderColor: background,
                }}
              />
              {!isLast && <span className={styles.line} />}
            </div>
            <div className={styles.body}>
              <Text weight="semibold">{node.status || status?.name}</Text>
              {node.dateTime && (
                <Text size={200} className={styles.meta}>
                  {formatStatusHistoryDate(node.dateTime)}
                </Text>
              )}
              {node.changedBy && (
                <Text size={200} className={styles.meta}>
                  {t('by', 'By')}: {node.changedBy}
                </Text>
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
};

export default WorkStatusHistory;
